import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const pad = (n) => String(n).padStart(2, '0');
const toInputDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const monthRange = (offset) => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const to = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { from: toInputDate(from), to: toInputDate(to) };
};

const PRESETS = [
  { key: 'thisMonth', label: 'החודש', range: () => monthRange(0) },
  { key: 'lastMonth', label: 'חודש קודם', range: () => monthRange(-1) },
  { key: 'last30', label: '30 יום', range: () => ({ from: toInputDate(new Date(Date.now() - 30 * 86400000)), to: toInputDate(new Date()) }) },
];

// טאב "עסקאות" - כל העסקאות שמומשו/בוטלו בטווח (לפי תאריך המימוש), בחנות
// ובאתר (WooCommerce). עסקה שבוטלה (ע"י בעל העסק, או הזמנת אונליין שבוטלה)
// מוצגת עם הסיבה ומי ביטל, ולא נספרת במחזור/בהנחה/בעמלה - הסיכומים למעלה
// מחושבים בשרת (spBOGetTransactions) רק על עסקאות במצב "מומש"
const TransactionsTab = () => {
  const [range, setRange] = useState(() => monthRange(0));
  const [preset, setPreset] = useState('thisMonth');
  const [businessId, setBusinessId] = useState('');
  const [status, setStatus] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    BOAPI.fetchBusinesses()
      .then((r) => { if (r?.[0]?.[0]?.code === 0) setBusinesses(r[1] || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, businessId, status]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await BOAPI.fetchTransactions({
        fromDate: `${range.from} 00:00:00`,
        toDate: `${range.to} 23:59:59`,
        businessId: businessId ? Number(businessId) : undefined,
        status: status || undefined,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        setRows(resp[1] || []);
        setTotals(resp[2]?.[0] || null);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת העסקאות');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (p) => {
    setPreset(p.key);
    setRange(p.range());
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.code, r.customerName, r.customerPhone, r.businessName, r.wooOrderId].some((v) => (v || '').toString().toLowerCase().includes(q)));
  }, [rows, search]);

  const exportCsv = () => {
    const header = ['תאריך מימוש', 'בית עסק', 'לקוח', 'טלפון', 'קוד', 'ערוץ', 'מספר הזמנה', 'סכום', 'הנחה', 'עמלה', 'סטטוס', 'תאריך ביטול', 'סיבת ביטול', 'בוטל ע"י'];
    const lines = visibleRows.map((r) => [
      fmtDateTime(r.redeemedAt), r.businessName, r.customerName, r.customerPhone, r.code,
      r.channel === 'online' ? 'אתר' : 'חנות', r.wooOrderId || '',
      r.amount ?? '', r.discountAmount ?? '', r.commissionAmount ?? '',
      r.status === 'cancelled' ? 'בוטל' : 'מומש', fmtDateTime(r.cancelledAt), r.cancelReason || '', r.cancelledByName || '',
    ]);
    const csv = [header, ...lines]
      .map((cols) => cols.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    // BOM - כדי שאקסל יפתח עברית נכון
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `עסקאות_${range.from}_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>עסקאות</h2>
        <button style={s.exportBtn} onClick={exportCsv} disabled={visibleRows.length === 0}>⬇ ייצוא לאקסל (CSV)</button>
      </div>
      <p style={s.hint}>
        עסקאות שמומשו בחנות ובאתר. עסקה שבוטלה (ע"י בעל העסק או הזמנה שבוטלה באתר) מוצגת עם הסיבה, ולא נכללת במחזור ובעמלה.
      </p>

      <div style={s.filters}>
        <div style={s.presets}>
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyPreset(p)} style={{ ...s.presetBtn, ...(preset === p.key ? s.presetBtnActive : {}) }}>{p.label}</button>
          ))}
        </div>
        <label style={s.filterLabel}>
          מתאריך
          <input type="date" value={range.from} max={range.to} onChange={(e) => { setPreset(''); setRange((r) => ({ ...r, from: e.target.value })); }} style={s.input} />
        </label>
        <label style={s.filterLabel}>
          עד תאריך
          <input type="date" value={range.to} min={range.from} onChange={(e) => { setPreset(''); setRange((r) => ({ ...r, to: e.target.value })); }} style={s.input} />
        </label>
        <label style={s.filterLabel}>
          בית עסק
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} style={s.input}>
            <option value="">כל בתי העסק</option>
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label style={s.filterLabel}>
          סטטוס
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={s.input}>
            <option value="">הכל</option>
            <option value="redeemed">מומש</option>
            <option value="cancelled">בוטל</option>
          </select>
        </label>
        <label style={{ ...s.filterLabel, flex: 1, minWidth: 200 }}>
          חיפוש
          <input placeholder="קוד, לקוח, טלפון, מספר הזמנה" value={search} onChange={(e) => setSearch(e.target.value)} style={s.input} />
        </label>
      </div>

      {totals && (
        <div style={s.totals}>
          <Stat label="עסקאות שמומשו" value={totals.redeemedCount || 0} color="#2e7d32" />
          <Stat label="מחזור" value={fmtMoney(totals.salesAmount)} color="#1f6feb" />
          <Stat label="הנחות ללקוחות" value={fmtMoney(totals.discountAmount)} color="#a06d00" />
          <Stat label="עמלה לחיוב" value={fmtMoney(totals.commissionAmount)} color="#8B5CF6" />
          <Stat label="בוטלו" value={`${totals.cancelledCount || 0} (${fmtMoney(totals.cancelledAmount)})`} color="#d93838" />
        </div>
      )}

      {error && <div style={s.errorBanner}>❌ {error}</div>}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : visibleRows.length === 0 ? (
        <div style={s.center}>אין עסקאות בטווח שנבחר</div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>תאריך</th>
                <th style={s.th}>בית עסק</th>
                <th style={s.th}>לקוח</th>
                <th style={s.th}>קוד</th>
                <th style={s.th}>ערוץ</th>
                <th style={s.th}>סכום</th>
                <th style={s.th}>הנחה</th>
                <th style={s.th}>עמלה</th>
                <th style={s.th}>סטטוס</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const cancelled = r.status === 'cancelled';
                return (
                  <tr key={r.id} style={cancelled ? s.rowCancelled : undefined}>
                    <td style={s.td}>{fmtDateTime(r.redeemedAt)}</td>
                    <td style={s.td}>{r.businessName}</td>
                    <td style={s.td}>
                      <div>{r.customerName}</div>
                      <div style={s.sub}>{r.customerPhone}</div>
                    </td>
                    <td style={{ ...s.td, fontFamily: 'monospace' }}>{r.code}</td>
                    <td style={s.td}>
                      {r.channel === 'online' ? <>🛒 אתר{r.wooOrderId ? <div style={s.sub}>הזמנה #{r.wooOrderId}</div> : null}</> : '🏪 חנות'}
                    </td>
                    <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{fmtMoney(r.amount)}</td>
                    <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{fmtMoney(r.discountAmount)}</td>
                    <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{fmtMoney(r.commissionAmount)}</td>
                    <td style={s.td}>
                      {cancelled ? (
                        <>
                          <span style={{ ...s.badge, ...s.badgeCancelled }}>בוטל</span>
                          <div style={s.sub}>{fmtDateTime(r.cancelledAt)}{r.cancelledByName ? ` · ${r.cancelledByName}` : ''}</div>
                          {r.cancelReason && <div style={s.sub}>סיבה: {r.cancelReason}</div>}
                        </>
                      ) : (
                        <span style={{ ...s.badge, ...s.badgeRedeemed }}>מומש</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div style={s.stat}>
    <div style={{ ...s.statValue, color }}>{value}</div>
    <div style={s.statLabel}>{label}</div>
  </div>
);

const s = {
  container:     { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:         { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  hint:          { color: '#8792a2', fontSize: 12.5, marginBottom: 16, lineHeight: 1.6 },
  exportBtn:     { padding: '8px 14px', background: '#fff', border: '1px solid #d8dee6', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1a1f36' },
  filters:       { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 },
  presets:       { display: 'flex', gap: 6, alignSelf: 'flex-end' },
  presetBtn:     { padding: '8px 12px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#697386' },
  presetBtnActive: { background: '#eef4ff', color: '#1f6feb', borderColor: '#1f6feb' },
  filterLabel:   { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4f566b' },
  input:         { padding: '7px 10px', border: '1px solid #d8dee6', borderRadius: 8, fontSize: 13, direction: 'rtl', minWidth: 140 },
  totals:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 },
  stat:          { border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 14px', background: '#fff' },
  statValue:     { fontSize: 20, fontWeight: 800 },
  statLabel:     { fontSize: 12, color: '#697386', marginTop: 2 },
  errorBanner:   { background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  center:        { padding: 40, textAlign: 'center', color: '#999' },
  tableWrap:     { overflowX: 'auto', border: '1px solid #e3e8ee', borderRadius: 12 },
  table:         { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:            { textAlign: 'right', padding: '10px 12px', background: '#f6f8fb', color: '#4f566b', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '1px solid #e3e8ee' },
  td:            { textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top', color: '#1a1f36' },
  rowCancelled:  { background: '#fff8f8' },
  strike:        { textDecoration: 'line-through', color: '#a3acb9' },
  sub:           { color: '#8792a2', fontSize: 11.5, marginTop: 2 },
  badge:         { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  badgeRedeemed: { background: '#e8f5e9', color: '#2e7d32' },
  badgeCancelled:{ background: '#fff1f1', color: '#d93838' },
};

export default TransactionsTab;
